/**
 * Pure helpers behind useAnalyticsIdentity: what is read from the profile
 * row and how it is coarsened before it reaches OpenPanel. No React, no
 * Supabase, so the guard tests can import this under any environment.
 */

/** The only profile columns analytics may read. Coarse by construction. */
export const ANALYTICS_PROFILE_COLUMNS = 'role, is_helper, profile_status, country_code, major_city, created_at';

export type AnalyticsIdentityRow = {
  role: string | null;
  is_helper: boolean | null;
  profile_status: string | null;
  country_code: string | null;
  major_city: string | null;
  created_at: string | null;
};

/** Account age as a bucket, so the exact signup date never leaves the app. */
export function accountAgeBucket(createdAt: string | null, now = Date.now()): string {
  if (!createdAt) return 'unknown';
  const days = (now - new Date(createdAt).getTime()) / 86_400_000;
  if (!Number.isFinite(days) || days < 0) return 'unknown';
  if (days < 7) return 'week_1';
  if (days < 30) return 'month_1';
  if (days < 90) return 'quarter_1';
  return 'older';
}

/** Same precedence as the post-auth router: admin, then helper, then client. */
export function roleFromProfile(row: Pick<AnalyticsIdentityRow, 'role' | 'is_helper'>): string {
  if (row.role === 'admin') return 'admin';
  if (row.role === 'helper' || row.is_helper) return 'helper';
  if (row.role === 'client' || row.is_helper === false) return 'client';
  return 'undeclared';
}

/** The traits identify() sends. Everything here is an enum, a code or a bucket. */
export function identityTraits(row: AnalyticsIdentityRow | null, now = Date.now()): Record<string, unknown> {
  return {
    role: row ? roleFromProfile(row) : 'undeclared',
    profile_status: row?.profile_status ?? null,
    market: row?.country_code ?? null,
    city: row?.major_city ?? null,
    account_age: accountAgeBucket(row?.created_at ?? null, now),
  };
}
